// Customer Display Manager
class CustomerDisplayManager {
  constructor() {
    this.customerWindow = null;
    this.isConnected = false;
  }

  // Open customer display window
  openCustomerDisplay() {
    if (this.customerWindow && !this.customerWindow.closed) {
      this.customerWindow.focus();
      return;
    }

    // Calculate window position for second screen
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    
    // Try to open on second monitor if available
    const leftPosition = screenWidth > 1920 ? screenWidth / 2 : 0;
    
    const windowFeatures = `
      width=${screenWidth > 1920 ? screenWidth / 2 : screenWidth},
      height=${screenHeight},
      left=${leftPosition},
      top=0,
      toolbar=no,
      menubar=no,
      scrollbars=no,
      resizable=yes,
      location=no,
      directories=no,
      status=no,
      fullscreen=yes
    `;

    this.customerWindow = window.open(
      '/customer-display',
      'CustomerDisplay',
      windowFeatures
    );

    if (this.customerWindow) {
      this.isConnected = true;
      
      // Handle window close
      this.customerWindow.addEventListener('beforeunload', () => {
        this.isConnected = false;
        this.customerWindow = null;
      });

      // Send initial data after window loads
      this.customerWindow.addEventListener('load', () => {
        this.sendWelcomeMessage();
      });
    }
  }

  // Open customer display specifically for sale system
  openSaleCustomerDisplay() {
    if (this.customerWindow && !this.customerWindow.closed) {
      this.customerWindow.focus();
      return;
    }

    // Auto-detect dual screen setup
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    
    // Position on right half for dual monitors, or left half for single monitor
    const leftPosition = screenWidth > 1920 ? screenWidth / 2 : 0;
    
    const windowFeatures = `
      width=${screenWidth > 1920 ? screenWidth / 2 : screenWidth},
      height=${screenHeight},
      left=${leftPosition},
      top=0,
      toolbar=no,
      menubar=no,
      scrollbars=no,
      resizable=yes,
      location=no,
      directories=no,
      status=no,
      fullscreen=yes
    `;

    this.customerWindow = window.open(
      '/sale-customer-display',
      'SaleCustomerDisplay',
      windowFeatures
    );

    if (this.customerWindow) {
      this.isConnected = true;
      
      // Handle window close
      this.customerWindow.addEventListener('beforeunload', () => {
        this.isConnected = false;
        this.customerWindow = null;
      });

      // Send initial data after window loads
      this.customerWindow.addEventListener('load', () => {
        this.sendWelcomeMessage();
      });
    }
  }

  // Close customer display window
  closeCustomerDisplay() {
    if (this.customerWindow && !this.customerWindow.closed) {
      this.customerWindow.close();
    }
    this.isConnected = false;
    this.customerWindow = null;
  }

  // Send cart updates to customer display
  updateCart(cart, total) {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        // Send cart data via custom event
        const event = new CustomEvent('cartUpdated', {
          detail: { cart, total }
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error updating customer display:', error);
        this.isConnected = false;
      }
    }
  }

  // Send company info to customer display
  updateCompanyInfo(companyInfo) {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('companyInfoUpdated', {
          detail: companyInfo
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error updating company info:', error);
      }
    }
  }

  // Send welcome message
  sendWelcomeMessage() {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('welcomeCustomer', {
          detail: { message: 'Welcome to our store!' }
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error sending welcome message:', error);
      }
    }
  }

  // Check if customer display is connected
  isDisplayConnected() {
    return this.isConnected && this.customerWindow && !this.customerWindow.closed;
  }

  // Send transaction complete message
  sendTransactionComplete(billDetails) {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('transactionComplete', {
          detail: billDetails
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error sending transaction complete:', error);
      }
    }
  }

  // Send bill confirmation to customer display
  sendBillConfirmation(billSummary) {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('billConfirmationUpdated', {
          detail: billSummary
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error sending bill confirmation:', error);
      }
    }
  }

  // Send custom message to display
  sendCustomMessage(message, type = 'info') {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('customMessage', {
          detail: { message, type }
        });
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error sending custom message:', error);
      }
    }
  }

  // Clear customer display and return to welcome screen
  clearCustomerDisplay() {
    if (this.isConnected && this.customerWindow && !this.customerWindow.closed) {
      try {
        const event = new CustomEvent('clearDisplay');
        this.customerWindow.dispatchEvent(event);
      } catch (error) {
        console.error('Error clearing customer display:', error);
      }
    }
  }
}

// Create singleton instance
const customerDisplayManager = new CustomerDisplayManager();

export default customerDisplayManager;
